import { RcIcon } from '@/components/atoms/RcIcon'
import { RcTrans } from '@/components/atoms/RcTrans'
import { ConfirmingButton } from '@/components/molecules/interactive/ConfirmingButton'
import { RcButton } from '@/components/molecules/interactive/RcButton'
import { RcIconButton } from '@/components/molecules/interactive/RcIconButton'
import { ShadowBox } from '@/components/molecules/interactive/ShadowBox'
import { EllipsisBox } from '@/components/molecules/text/EllipsisBox'
import { RcAvatar } from '@/components/molecules/text/RcAvatar'
import { UserAvatar } from '@/components/organisms/avatar/UserAvatar'
import { LabeledFieldHeader } from '@/components/organisms/form/LabeledField'
import { ModalTrigger } from '@/components/organisms/modal/ModalTrigger'
import { useChat } from '@/components/providers/site/ChatProvider'
import { useNavigation } from '@/core/hooks/useNavigation'
import { CompetitionRoute, RootRoute } from '@/core/route-keys'
import { useChatRoomDocument } from '@/entity/chat/useChatRoomDocument'
import { ContactFlowLink } from '@/flows/Site/ContactFlow/ContactFlowLink'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { EntityType } from '@rallycry/api-suite-typescript'
import { ChatDocument } from '@rallycry/social-api-typescript'
import { ChatParticipantAdd } from './ChatParticipantAdd'
import { ChatMembersList } from './ChatMembersList'

export const ChatInfo = ({
  room,
  onClose
}: {
  room?: ChatDocument
  onClose: () => void
}) => {
  const { handleShowChat, handleCloseChat } = useChat()
  const { getPath, navTo } = useNavigation()
  const {
    members,
    addParticipant,
    removeParticipant,
    leaveChat,
    isModerator,
    attributes: { entityId, entityType, childId, childType }
  } = useChatRoomDocument({
    idOrKey: room?.id
  })

  const handleLeaveChat = async () => {
    await leaveChat()
    handleCloseChat(room?.id!)
    navTo({ root: RootRoute.Chat })
  }

  const getEntityString = (entityType: EntityType) => {
    if (entityType === EntityType.COMMUNITY) {
      return <RcTrans i18nKey='shared.community' />
    } else if (entityType === EntityType.COMPETITION) {
      return <RcTrans i18nKey='shared.competition' />
    } else if (entityType === EntityType.BRACKET_MATCH) {
      return <RcTrans i18nKey='shared.bracket-match' />
    }
    return ''
  }

  const getEntityPath = (
    entityType: EntityType,
    entityId: number,
    childType?: EntityType,
    childId?: number
  ) => {
    const root =
      entityType === EntityType.COMMUNITY
        ? RootRoute.Community
        : entityType === EntityType.COMPETITION
          ? RootRoute.Competition
          : undefined

    if (!root) return undefined

    let subRoute
    let subId

    if (childType === EntityType.BRACKET_MATCH) {
      subRoute = CompetitionRoute.Match
      subId = childId
    }

    return getPath({ root, rootId: entityId, subRoute, subId })
  }

  const handleNavigate = () => {
    const path = getEntityPath(entityType, entityId, childType, childId)
    if (path) {
      handleShowChat(room?.id, true)
      navTo(path)
    }
    onClose()
  }

  return (
    <Stack
      sx={theme => ({
        height: '100%',
        maxHeight: '100%'
      })}
      spacing={0}
    >
      <Box pb={5}>
        <LabeledFieldHeader label={getEntityString(childType || entityType)} />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleNavigate}>
              <Stack
                direction='row'
                alignItems='center'
                width='100%'
                spacing={4}
              >
                <RcAvatar variant='rounded' size='small' src={room?.image} />

                <EllipsisBox lineClamp={2} breakAll={false}>
                  <Typography>{room?.name}</Typography>
                </EllipsisBox>
              </Stack>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <LabeledFieldHeader
          label={<RcTrans i18nKey='shared.members-chat-label' />}
        />

        {isModerator ? (
          <ModalTrigger
            title={<RcTrans i18nKey='shared.add-participant-label' />}
            activation={handleOpen => (
              <RcIconButton
                icon={['fal', 'user-plus']}
                size='extraSmall'
                onClick={handleOpen}
              />
            )}
          >
            {({ handleClose }) => (
              <ChatParticipantAdd
                onAdd={async user => {
                  if (user) {
                    await addParticipant(user)
                    handleClose()
                  }
                }}
              />
            )}
          </ModalTrigger>
        ) : null}
      </Stack>
      <ShadowBox sx={{ overflow: 'auto', flexGrow: 1 }}>
        <ChatMembersList
          members={members}
          isModerator={isModerator}
          removeParticipant={removeParticipant}
        />
      </ShadowBox>

      <Stack direction='column' spacing={3} mt={2} ml={-1}>
        <ContactFlowLink>
          <RcButton
            startIcon={
              <RcIcon icon={['fal', 'triangle-exclamation']} size='sm' />
            }
            variant='text'
            size='small'
            sx={{ color: 'error.main' }}
          >
            <RcTrans i18nKey='shared.report-chat-label' />
          </RcButton>
        </ContactFlowLink>
        <ConfirmingButton
          onClick={handleLeaveChat}
          variant='text'
          size='small'
          fullWidth={false}
          sx={{ color: 'error.main' }}
          startIcon={<RcIcon icon={['fal', 'sign-out']} size='sm' />}
          message={<RcTrans i18nKey='shared.leave-chat-confirmation' />}
        >
          <RcTrans i18nKey='shared.leave-chat-label' />
        </ConfirmingButton>
      </Stack>
    </Stack>
  )
}
